// What the Story room's pages share: a modal sheet, an entity's dot in its colour (set through the CSSOM: the CSP allows
// no style attributes), the empty state, dates as the device writes them, and the datetime-local field's readings.
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { entityCSSColor } from '@writing-suite/core';
import { components, deviceZone, formatDate, instant } from '@writing-suite/core/story';
import { useWorkspace } from '../../shell/workspace.ts';

/** How faint Story draws a scene the contents' filter leaves out (Story highlights, never hides: `storyEmphasis`). */
export const STORY_FADED = 0.22;

/** `storyEmphasis`: a scene's opacity, 1 or faded when the filter leaves it out. A mark that already has an opacity
 *  multiplies it (an SVG presentation attribute); one without takes the `filtered-out` class (`faded`). */
export function useStoryEmphasis(): { emphasis: (fragmentId: string) => number; faded: (fragmentId: string) => string } {
  const ws = useWorkspace();
  const filter = ws?.filter?.view ? ws.filter : null;
  const emphasis = (id: string) => (!filter || filter.matchesFragment(id) ? 1 : STORY_FADED);
  return { emphasis, faded: (id) => (emphasis(id) < 1 ? ' filtered-out' : '') };
}

/** A modal sheet (`<dialog>`): Escape or the scrim closes it. `wide` for the name generator's two columns. */
export function StorySheet({ title, onClose, children, className = '', labelledBy }: { title: string; onClose: () => void; children: ReactNode; className?: string; labelledBy?: string }) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
    return () => d?.close();
  }, []);
  const id = labelledBy ?? `sheet-${title.replace(/\W+/g, '-').toLowerCase()}`;
  return (
    <dialog
      ref={ref}
      className={`story-sheet ${className}`}
      aria-labelledby={id}
      onCancel={(e) => {
        e.preventDefault();
        onClose();
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="story-sheet-body">
        {labelledBy ? null : (
          <h2 id={id} className="story-sheet-title">
            {title}
          </h2>
        )}
        {children}
      </div>
    </dialog>
  );
}

/** An entity's dot in its colour (the first hue for one stored before colours). */
export function EntityDot({ color, size }: { color: string | undefined | null; size?: 'large' }) {
  const css = entityCSSColor(color);
  return <span className={`entity-dot${size ? ` ${size}` : ''}`} aria-hidden="true" ref={(el) => el?.style.setProperty('--dot', css)} />;
}

export function StoryEmpty({ title = 'Nothing to show yet', children }: { title?: string; children: ReactNode }) {
  return (
    <div className="story-empty" role="note">
      <p className="story-empty-title">{title}</p>
      <p>{children}</p>
    </div>
  );
}

/** "Scene 4 · 3 Oct 1884, 23:00" as the device writes a story time (a clock reading in its own zone). */
export const storyDate = (ms: number, time = true) => formatDate(ms, deviceZone(), 'abbreviated', time ? 'shortened' : 'omitted', navigatorLocale());

export const navigatorLocale = () => (typeof navigator !== 'undefined' && navigator.language) || 'en-US';

const pad = (n: number, w = 2) => String(n).padStart(w, '0');

/** A story time as a datetime-local field shows it (the device's clock). */
export function toLocalInput(ms: number): string {
  const c = components(ms, deviceZone());
  return `${pad(c.year, 4)}-${pad(c.month)}-${pad(c.day)}T${pad(c.hour)}:${pad(c.minute)}`;
}

/** A datetime-local field's reading as an instant (the device's clock), or null. */
export function fromLocalInput(value: string): number | null {
  const m = /^(\d{4,})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/.exec(value);
  if (!m) return null;
  return instant({ year: +m[1], month: +m[2], day: +m[3], hour: +m[4], minute: +m[5], second: 0, ms: 0 }, deviceZone());
}

/** A date field's reading ("1843-05-02") as the device's midnight, and back. */
export function fromDateInput(value: string): number | null {
  const m = /^(\d{4,})-(\d{2})-(\d{2})$/.exec(value);
  return m ? instant({ year: +m[1], month: +m[2], day: +m[3], hour: 0, minute: 0, second: 0, ms: 0 }, deviceZone()) : null;
}

export function toDateInput(ms: number): string {
  const c = components(ms, deviceZone());
  return `${pad(c.year, 4)}-${pad(c.month)}-${pad(c.day)}`;
}

export const plural = (n: number, one: string, many = `${one}s`) => `${n.toLocaleString('en-GB')} ${n === 1 ? one : many}`;

/** A control's value shown at once when it's changed, until the project (rebuilt a moment after an edit) has it. */
export function useShown<T>(stored: T): [T, (v: T) => void] {
  const [pending, setPending] = useState<{ value: T; over: T } | null>(null);
  // Another value arriving (this edit, or another device's) ends the override.
  const shown = pending && Object.is(pending.over, stored) ? pending.value : stored;
  return [shown, (value: T) => setPending({ value, over: stored })];
}
